import { useCallback, useEffect, useState } from 'react';

const cache = new Map();

function useResource(url) {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState(() => ({ url, data: url ? cache.get(url) ?? null : null, loading: Boolean(url && !cache.has(url)), error: '' }));

  useEffect(() => {
    if (!url) return;
    const controller = new AbortController();
    fetch(url, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Indholdet kunne ikke indlæses (${response.status}).`);
        return response.json();
      })
      .then((data) => {
        cache.set(url, data);
        setState({ url, data, loading: false, error: '' });
      })
      .catch((error) => {
        if (error.name !== 'AbortError') setState({ url, data: cache.get(url) ?? null, loading: false, error: error.message });
      });

    return () => controller.abort();
  }, [url, revision]);

  const retry = useCallback(() => setRevision((value) => value + 1), []);
  const visible = state.url === url ? state : { url, data: cache.get(url) ?? null, loading: Boolean(url && !cache.has(url)), error: '' };
  return { ...visible, retry };
}

// Bare endpoint: small, fixed collections rendered in full, always an array.
export function useCmsCollection(collection) {
  const resource = useResource(collection ? `/_api/public/content/${encodeURIComponent(collection)}` : '');
  return { ...resource, items: Array.isArray(resource.data) ? resource.data : [] };
}

// V2 endpoint: server-side term/field filtering, ordering and pagination, always { items, total }.
export function useCmsList(collection, params = {}, enabled = true) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined) return;
    if (Array.isArray(value)) value.forEach((part) => query.append(key, String(part)));
    else query.set(key, String(value));
  });
  const url = enabled && collection ? `/_api/public/v2/content/${encodeURIComponent(collection)}${query.size ? `?${query}` : ''}` : '';
  const resource = useResource(url);
  return { ...resource, items: resource.data?.items ?? [], total: resource.data?.total ?? 0 };
}

// Gallery entries are resolved by their CMS slug, not by an image list baked into a page.
export function useCmsSlug(slug, language = 'da') {
  const resource = useResource(slug ? `/_api/public/by-slug/${encodeURIComponent(language)}/${encodeURIComponent(slug)}` : '');
  return { ...resource, item: resource.data };
}
